import React, { useState } from 'react';
import { SiInstagram } from 'react-icons/si';
import { ImageGallery } from './ImageGallery';
import { isYouTubeUrl, isInstagramUrl, extractYouTubeId } from './imageUtils';
import type { MediaItem } from './types';

function YouTubeThumbnail({ url }: { url: string }) {
  const videoId = extractYouTubeId(url);
  if (!videoId) return null;
  return (
    <img
      src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
      alt="YouTube thumbnail"
      className="w-full h-full object-cover"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  );
}

/** Thumbnail for a pasted video link: a YouTube poster image, or an Instagram glyph (IG has no public thumbnail endpoint). */
function LinkThumbnail({ url }: { url: string }) {
  if (isInstagramUrl(url)) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white">
        <SiInstagram className="w-5 h-5" aria-hidden />
      </div>
    );
  }
  return <YouTubeThumbnail url={url} />;
}

export interface MediaFormProps {
  /** Uploaded / hosted images & videos (excluding YouTube links). */
  images: MediaItem[];
  /** Video links — YouTube videos and/or Instagram reels/posts (stored as raw URLs; kind is detected by URL). */
  youtubeLinks: string[];
  onImagesChange: (images: MediaItem[]) => void;
  onYouTubeLinksChange: (links: string[]) => void;
  maxImages?: number;
  className?: string;
}

/**
 * Controlled media form: drag-and-drop image/video upload (with reordering,
 * captions and copyright) plus a YouTube link manager.
 *
 * Shared by the event creator and the artist profile editor. Uploads are kept
 * as blob URLs in the shared pending-file registry; the consumer uploads them
 * to the server at save time and swaps the blob URLs for the final URLs.
 */
export const MediaForm: React.FC<MediaFormProps> = ({
  images,
  youtubeLinks,
  onImagesChange,
  onYouTubeLinksChange,
  maxImages = 10,
  className = '',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');

  const handleAddVideoLink = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (!isYouTubeUrl(trimmed) && !isInstagramUrl(trimmed)) {
      setInputError('Please enter a valid YouTube or Instagram link (e.g. youtube.com/watch?v=…, youtu.be/…, or instagram.com/reel/…)');
      return;
    }
    if (youtubeLinks.includes(trimmed)) {
      setInputError('This link has already been added');
      return;
    }
    onYouTubeLinksChange([...youtubeLinks, trimmed]);
    setInputValue('');
    setInputError('');
  };

  const handleRemoveVideoLink = (index: number) => {
    onYouTubeLinksChange(youtubeLinks.filter((_, i) => i !== index));
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddVideoLink();
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Image / video upload */}
      <div>
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            💡 <strong>Tip:</strong> The first image will be the main image shown in cards and previews.
          </p>
        </div>
        <ImageGallery
          images={images}
          onChange={onImagesChange}
          maxImages={maxImages}
        />
      </div>

      {/* Helper info for uploaded files */}
      {images.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            ✓ Great! You&apos;ve added {images.length} {images.length === 1 ? 'file' : 'files'}.
            {images.length < 3 && ' Consider adding more photos or videos to showcase yourself better.'}
          </p>
        </div>
      )}

      {/* Video links (YouTube + Instagram) */}
      <div className="border border-gray-200 rounded-xl p-5 space-y-4">
        <div>
          <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <span>▶</span> Videos &amp; reels
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            Paste YouTube video or Instagram reel links. They will be displayed alongside your images.
          </p>
        </div>

        {/* Existing video links */}
        {youtubeLinks.length > 0 && (
          <ul className="space-y-3">
            {youtubeLinks.map((url, index) => (
              <li key={index} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2">
                <div className="w-20 h-12 rounded overflow-hidden flex-shrink-0 bg-gray-200">
                  <LinkThumbnail url={url} />
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={url}
                  className="flex-1 min-w-0 text-sm text-balloon hover:underline truncate"
                >
                  {url}
                </a>
                <button
                  type="button"
                  onClick={() => handleRemoveVideoLink(index)}
                  aria-label="Remove video link"
                  className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1 rounded"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Add new link */}
        <div className="space-y-1.5">
          <div className="flex gap-2">
            <input
              type="url"
              value={inputValue}
              onChange={(e) => { setInputValue(e.target.value); setInputError(''); }}
              onKeyDown={handleInputKeyDown}
              placeholder="https://www.youtube.com/watch?v=…  or  https://www.instagram.com/reel/…"
              aria-label="YouTube or Instagram URL"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg outline-none transition-colors focus:ring-2 focus:ring-balloon/30 focus:border-balloon ${inputError ? 'border-red-400' : 'border-gray-300'}`}
            />
            <button
              type="button"
              onClick={handleAddVideoLink}
              disabled={!inputValue.trim()}
              className="px-4 py-2 text-sm font-medium bg-balloon text-white rounded-lg hover:bg-balloon/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
          </div>
          {inputError && (
            <p className="text-xs text-red-600" role="alert">{inputError}</p>
          )}
        </div>
      </div>
    </div>
  );
};
